import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { api, apiFetch, tokenStore, type ApiError } from '../../api/client';
import { Alert, Field } from '../../components/Field';
import { VideoUpload } from '../../components/VideoUpload';
import { StatusPill } from '../../components/StatusPill';
import { titleCase } from '../../lib/format';
import { megabytes, useLimits } from '../../api/limits';

interface ContentRow {
  id: string;
  title: string;
  kind: string;
  status: string;
  versions: number;
  usedByLessons: number;
  usedByCourses: number;
  sizeBytes: number | null;
  scanStatus: string | null;
  servable: boolean;
  reviewNote: string | null;
  updatedAt: string;
}

function bytes(value: number | null): string {
  if (!value) return '—';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

const PAGE_SIZE = 50;

/** "3 lessons · 1 course", or "Unused" — a cover counts as used by its course. */
function usage(item: ContentRow): string {
  const parts = [
    item.usedByLessons ? `${item.usedByLessons} lesson${item.usedByLessons === 1 ? '' : 's'}` : '',
    item.usedByCourses ? `${item.usedByCourses} course${item.usedByCourses === 1 ? '' : 's'}` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Unused';
}

export function ContentLibrary() {
  const [rows, setRows] = useState<ContentRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [limitations, setLimitations] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (status) params.set('status', status);
    api<{ items: ContentRow[]; totalItems: number }>(`/content?${params}`)
      .then((result) => {
        setRows(result.items);
        setTotal(result.totalItems);
      })
      .catch((caught) => setError(caught as ApiError));
  }, [status, page]);

  useEffect(load, [load]);

  async function act(path: string, body: unknown, message: string) {
    setError(null);
    setNotice(null);
    try {
      await api(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
      setNotice(message);
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Content library</h1>
        <p className="page__lede">
          Every uploaded file and authored text, with its version history and scan state. Content
          must be published before a course that uses it can go live.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      <div
        className="tiles"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
      >
        <Upload
          onDone={(message) => {
            setNotice(message);
            load();
          }}
          onError={setError}
        />
        <VideoUpload
          onDone={(video) => {
            // Say what was actually read from the file, and what the processor
            // on this host could not do, rather than a bare "uploaded".
            const read = video.durationSeconds
              ? `${video.durationSeconds}s${video.width ? `, ${video.width}×${video.height}` : ''}`
              : 'duration unknown';
            setNotice(`Added "${video.title}" (${read}).`);
            setLimitations(video.limitations);
            load();
          }}
          onError={setError}
        />
        <TextContent
          onDone={(message) => {
            setNotice(message);
            load();
          }}
          onError={setError}
        />
      </div>

      {limitations.length > 0 ? (
        <Alert kind="info" title="What this host could not do" items={limitations} />
      ) : null}

      <div className="card">
        <div className="card__head">
          <span className="card__title">
            {rows ? `${total.toLocaleString()} item${total === 1 ? '' : 's'}` : 'Loading…'}
          </span>
          <select
            className="cat__sort"
            aria-label="Filter by status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            {['', 'DRAFT', 'REVIEW', 'APPROVED', 'PUBLISHED', 'ARCHIVED'].map((value) => (
              <option key={value} value={value}>
                {value === '' ? 'All statuses' : value}
              </option>
            ))}
          </select>
        </div>

        {!rows ? (
          <div className="spinner">Loading content…</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            No content yet. Upload a file or write a rich-text lesson above.
          </div>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Title</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Scan</th>
                <th>Versions</th>
                <th>Used by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.title}</strong>
                    {item.reviewNote ? (
                      <>
                        <br />
                        <span className="muted" style={{ fontSize: 12 }}>
                          Reviewer: {item.reviewNote}
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td className="muted">
                    {titleCase(item.kind)}
                    <br />
                    <span style={{ fontSize: 12 }}>{bytes(item.sizeBytes)}</span>
                  </td>
                  <td>
                    <StatusPill status={item.status} />
                  </td>
                  <td>
                    {item.servable ? (
                      <span className="pill pill--active">Servable</span>
                    ) : (
                      <span
                        className="pill pill--inactive"
                        title="Quarantined until a scanner clears it"
                      >
                        {item.scanStatus ?? 'Pending'}
                      </span>
                    )}
                  </td>
                  <td className="muted">{item.versions}</td>
                  <td className="muted">{usage(item)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {item.status === 'DRAFT' ? (
                      <button
                        className="iconbtn"
                        onClick={() =>
                          act(`/content/${item.id}/submit`, {}, 'Submitted for review.')
                        }
                      >
                        Submit
                      </button>
                    ) : null}
                    {item.status === 'APPROVED' ? (
                      <button
                        className="iconbtn"
                        onClick={() => act(`/content/${item.id}/publish`, {}, 'Published.')}
                      >
                        Publish
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {total > PAGE_SIZE ? (
          <div className="pager">
            <button
              className="iconbtn"
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
            >
              ← Previous
            </button>
            <span className="muted">
              Page {page} of {pages} · {total.toLocaleString()} items
            </span>
            <button
              className="iconbtn"
              disabled={page >= pages}
              onClick={() => setPage((value) => value + 1)}
            >
              Next →
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}

function Upload({
  onDone,
  onError,
}: {
  onDone: (message: string) => void;
  onError: (error: ApiError) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const limits = useLimits();

  async function send(event: FormEvent) {
    event.preventDefault();
    const file = input.current?.files?.[0];
    if (!file) return;

    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    form.append('title', file.name);

    try {
      // FormData sets its own multipart boundary, so the fetch wrapper's JSON
      // default must not be applied here.
      const response = await apiFetch('/content/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenStore.access ?? ''}` },
        body: form,
      });
      const payload = await response.json();
      if (!response.ok) throw { ...payload, status: response.status } as ApiError;
      onDone(`Uploaded ${file.name}.`);
      if (input.current) input.current.value = '';
    } catch (caught) {
      onError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  // The picker filters by the same list the server validates against, so a
  // file it would refuse is harder to choose in the first place.
  const accept = (limits?.upload.extensions ?? []).map((extension) => `.${extension}`).join(',');

  return (
    <form className="tile" onSubmit={send}>
      <div className="tile__label">Upload a file</div>
      <input
        ref={input}
        className="field__input"
        type="file"
        style={{ margin: '10px 0' }}
        accept={accept}
        required
        aria-describedby="sx-upload-limits"
      />
      <ul className="tile__rules" id="sx-upload-limits">
        <li>
          <strong>Up to {megabytes(limits?.upload.maxFileMb ?? 100)}</strong> per file.
        </li>
        <li>
          <strong>Accepted:</strong>{' '}
          {(limits?.upload.extensions ?? []).length > 0
            ? limits!.upload.extensions.map((extension) => `.${extension}`).join(' ')
            : 'PDF, Office documents, images, video and captions'}
        </li>
        <li>
          The file's own bytes decide its type, not its name — renaming something to .pdf will not
          get it through.
        </li>
        <li>
          Large video belongs in <strong>Upload a video</strong> beside this, which sends it in
          resumable pieces.
        </li>
      </ul>
      <button className="btn btn--sm" type="submit" disabled={busy}>
        {busy ? 'Uploading…' : 'Upload'}
      </button>
    </form>
  );
}

function TextContent({
  onDone,
  onError,
}: {
  onDone: (message: string) => void;
  onError: (error: ApiError) => void;
}) {
  const [form, setForm] = useState({ title: '', body: '' });
  const [busy, setBusy] = useState(false);
  const limits = useLimits();

  const maxTitle = limits?.text.maxTitleChars ?? 250;
  const maxBody = limits?.text.maxBodyChars ?? 200_000;
  // Counted rather than clipped: silently truncating somebody's lesson at
  // 200,000 characters would lose the end of it without saying so.
  const overBody = form.body.length > maxBody;

  async function send(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api('/content/text', { method: 'POST', body: JSON.stringify(form) });
      onDone(`Created “${form.title}”.`);
      setForm({ title: '', body: '' });
    } catch (caught) {
      onError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="tile" onSubmit={send}>
      <div className="tile__label">Write rich text</div>
      <Field
        label="Title"
        required
        maxLength={maxTitle}
        value={form.title}
        onChange={(event) => setForm({ ...form, title: event.target.value })}
      />
      <div className="field">
        <label className="field__label" htmlFor="body">
          Body (HTML)
        </label>
        <textarea
          id="body"
          className="field__input"
          rows={3}
          required
          value={form.body}
          onChange={(event) => setForm({ ...form, body: event.target.value })}
          placeholder="<h2>Heading</h2><p>Text…</p>"
        />
        <p className="field__hint">
          Sanitised on save: script, handlers and unsafe URLs are removed.
        </p>
        <p className={`field__hint${overBody ? ' field__hint--warn' : ''}`} role="status">
          {form.body.length.toLocaleString()} of {maxBody.toLocaleString()} characters
          {overBody ? ' — too long to save. Split it across two items.' : ''}
        </p>
      </div>
      <button className="btn btn--sm" type="submit" disabled={busy || overBody}>
        {busy ? 'Saving…' : 'Create'}
      </button>
    </form>
  );
}
